<template>
  <MainLayout :title="trans('Cart')">
    <div
      class="sticky top-0 z-10 bg-theme-background"
      style="padding-top: var(--safe-area-inset-top)"
    >
      <div class="flex items-center p-4">
        <ADeepCircleButton
          class="flex-shrink-0"
          @click="router.push({ name: 'menu' })"
        >
          <template #icon><ArrowLeftBold /></template>
        </ADeepCircleButton>
        <div
          class="flex-1 truncate px-4 text-center font-primary text-lg font-medium"
        >
          {{ trans('Cart') }}
        </div>
        <div class="h-10 w-10 flex-shrink-0"></div>
      </div>
    </div>
    <form
      v-if="cartStore.cartItems.length > 0"
      class="mx-auto max-w-xl p-4 pb-8"
      @submit.prevent="placeOrder"
    >
      <div>
        <div>
          <div
            v-for="(cartItem, cartItemIndex) in cartStore.cartItems"
            :key="cartItemIndex"
            class="mb-5 flex justify-between"
          >
            <div>
              <div>
                {{ localize(cartItem.product.name) }}
                <template v-if="localize(cartItem.variant.name)"
                  >- {{ localize(cartItem.variant.name) }}</template
                >
              </div>
              <div v-if="cartItem.modifiers.length > 0" class="opacity-70">
                {{
                  cartItem.modifiers
                    .map(({ name }) => localize(name))
                    .join(', ')
                }}
              </div>
              <div v-if="cartItem.price > 0" class="font-semibold">
                {{ formatPrice(cartItem.price * cartItem.quantity) }}
                <span v-if="cartItem.quantity > 1" class="text-sm">
                  ({{ formatPrice(cartItem.price) }} x {{ cartItem.quantity }})
                </span>
              </div>
              <div class="mt-2 flex items-center">
                <QuantityPicker
                  :model-value="cartItem.quantity"
                  :min="0"
                  @update:model-value="
                    (quantity) => setQuantityForCartItem(cartItem, quantity)
                  "
                />

                <button
                  type="button"
                  class="rounded p-1 text-theme-primary/50 transition hover:text-red-500 dir-ltr:ml-3 dir-rtl:mr-3"
                  @click="setQuantityForCartItem(cartItem, 0)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="h-5 w-5"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div class="ml-3">
              <div class="w-20">
                <div class="relative pb-[100%]">
                  <AImage
                    class="absolute inset-0 h-full w-full overflow-hidden rounded-md object-cover object-center"
                    :src="
                      cartItem.product.image?.thumbnailUrl ||
                      restaurant.fallbackProductImage.url
                    "
                    :alt="localize(cartItem.product.name) ?? ''"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="cartStore.cartItemsTotalPrice > 0" class="mt-6 space-y-1">
            <div class="flex justify-between">
              <span>{{ trans('Subtotal') }}</span>
              <span>{{ formatPrice(cartStore.cartItemsTotalPrice) }}</span>
            </div>
            <div
              v-for="priceAdjustment in cartPriceAdjustments"
              :key="priceAdjustment.id"
              class="flex justify-between"
            >
              <span
                >{{ localize(priceAdjustment.name) }} ({{
                  priceAdjustment.rate
                }}%)</span
              >
              <span>{{ formatPrice(priceAdjustment.amount) }}</span>
            </div>
            <div class="flex justify-between font-medium">
              <span>{{ trans('Total amount') }}</span>
              <span>{{ formatPrice(cartTotalPrice) }}</span>
            </div>
          </div>
        </div>
        <div class="mt-10 space-y-6">
          <div v-if="showOrderTypeSelector">
            <label class="mb-1 inline-block"
              >{{ trans('Order type') }}
              <span class="text-red-500">*</span></label
            >

            <div class="flex flex-col">
              <OptionGroupItem
                v-for="orderType in orderTypes"
                :key="orderType.id"
                :is-selected="selectedOrderTypeId === orderType.id"
                @click="selectedOrderTypeId = orderType.id"
              >
                {{ localize(orderType.name) }}

                <template #description>
                  {{ localize(orderType.description) }}
                </template>
              </OptionGroupItem>
            </div>
          </div>

          <template v-if="selectedOrderType">
            <div v-if="askName" class="flex flex-col">
              <label for="customerName" class="mb-1 inline-block"
                >{{ trans('Name') }}
                <span
                  v-if="selectedOrderType.isNameRequired"
                  class="text-red-500"
                  >*</span
                ></label
              >
              <input
                id="customerName"
                v-model="customerName"
                :required="selectedOrderType.isNameRequired"
                type="text"
                class="menu-form-input"
              />
            </div>
            <div v-if="askPhone" class="flex flex-col">
              <label for="customerPhone" class="mb-1 inline-block"
                >{{ trans('Phone') }}
                <span
                  v-if="selectedOrderType.isPhoneRequired"
                  class="text-red-500"
                  >*</span
                ></label
              >
              <input
                id="customerPhone"
                v-model="customerPhone"
                :required="selectedOrderType.isPhoneRequired"
                type="text"
                class="menu-form-input"
              />
              <div class="mt-1 text-sm text-theme-text">
                {{
                  trans(
                    'We will contact you if we have questions regarding your order.',
                  )
                }}
              </div>
            </div>
            <div v-if="askEmail" class="flex flex-col">
              <label for="customerEmail" class="mb-1 inline-block"
                >{{ trans('Email') }}
                <span
                  v-if="selectedOrderType.isEmailRequired"
                  class="text-red-500"
                  >*</span
                ></label
              >
              <input
                id="customerEmail"
                v-model="customerEmail"
                :required="selectedOrderType.isEmailRequired"
                type="email"
                class="menu-form-input"
              />
              <div class="mt-1 text-sm text-theme-text">
                {{
                  trans('We will send you order status updates to this email.')
                }}
              </div>
            </div>
            <div v-if="askAddress" class="flex flex-col">
              <label for="customerAddress" class="mb-1 inline-block"
                >{{ trans('Address') }}
                <span class="text-red-500">*</span></label
              >
              <textarea
                id="customerAddress"
                v-model="customerAddress"
                class="menu-form-input"
                required
                :placeholder="trans('City, area, street, building, apartment')"
              />
            </div>
            <div
              v-if="selectedOrderType.type === 'TABLE'"
              class="flex flex-col"
            >
              <label for="customerTable" class="mb-1 inline-block">
                {{ trans('Table number') }}
                <span class="text-red-500">*</span></label
              >
              <input
                id="customerTable"
                v-model="customerTable"
                required
                type="text"
                class="menu-form-input"
                :disabled="isCustomerTableReadonly"
              />
            </div>
            <div
              v-if="
                selectedOrderType.name.en
                  ?.toLowerCase()
                  .split(' ')
                  .includes('car')
              "
              class="flex flex-col"
            >
              <label for="customerCarPlateNumber" class="mb-1 inline-block">
                {{ trans('Car plate number') }}
                <span class="text-red-500">*</span></label
              >
              <input
                id="customerCarPlateNumber"
                v-model="customerCarPlateNumber"
                required
                type="text"
                class="menu-form-input"
                :placeholder="trans('Enter your car plate number')"
              />
            </div>
            <div v-if="selectedOrderType.isNoteEnabled" class="flex flex-col">
              <label for="customerNote" class="mb-1 inline-block">{{
                trans('Special instructions')
              }}</label>
              <textarea
                id="customerNote"
                v-model="customerNote"
                class="menu-form-input"
                :placeholder="trans('Add a note or any instructions')"
              />
            </div>
          </template>
          <div v-if="orderPaymentTypes.length > 0 && selectedOrderTypeId">
            <label class="mb-1 inline-block"
              >{{ trans('Payment type') }}
              <span class="text-red-500">*</span></label
            >

            <div class="flex flex-col">
              <OptionGroupItem
                v-for="orderPaymentType in orderPaymentTypes"
                :key="orderPaymentType.id"
                :is-selected="
                  selectedOrderPaymentTypeId === orderPaymentType.id
                "
                @click="selectedOrderPaymentTypeId = orderPaymentType.id"
              >
                {{ localize(orderPaymentType.name) }}

                <template #description>
                  {{ localize(orderPaymentType.description) }}
                </template>
              </OptionGroupItem>
            </div>
          </div>
        </div>

        <div
          class="mx-auto mt-8 max-w-xs text-center text-sm text-theme-text/80"
        >
          By placing an order, I agree to Apetito Menu's
          <a
            href="https://apetitomenu.com/terms-of-service-for-consumers"
            target="_blank"
            class="text-theme-text hover:underline"
            >Terms of Service</a
          >
          and
          <a
            href="https://apetitomenu.com/privacy-policy-for-consumers"
            target="_blank"
            class="text-theme-text hover:underline"
            >Privacy Policy</a
          >.
        </div>

        <div class="mt-8">
          <div>
            <button
              type="submit"
              class="menu-button-primary w-full"
              :disabled="placingOrder"
            >
              <template v-if="placingOrder">
                {{ trans('Placing order') }}...
              </template>
              <template v-else-if="selectedOrderPaymentType?.isOnlinePayment">
                {{ trans('Place order & Pay online') }}
              </template>
              <template v-else> {{ trans('Place order') }} </template>
            </button>
          </div>
        </div>
        <div class="h-24"></div>
      </div>
    </form>
    <div v-else class="p-4">{{ trans('Your cart is empty') }}...</div>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@menu/Shared/MainLayout.vue';
import ADeepCircleButton from '@menu/Shared/ADeepCircleButton.vue';
import QuantityPicker from '@menu/Shared/QuantityPicker.vue';
import AImage from '@menu/Shared/AImage.vue';
import axios from 'axios';
import OptionGroupItem from '@menu/Shared/OptionGroupItem.vue';
import ArrowLeftBold from '@/phosphoricons/ArrowLeftBold.vue';
import { trans } from '@menu/Utils/i18n';
import { localize } from '@menu/Utils/i18n';
import { CartItem, useCartStore } from '@menu/Stores/cart';
import formatPrice from '@menu/Utils/formatPrice';
import getVisitorUuid from '@menu/Utils/getVisitorUuid';
import { computed, onMounted, ref } from 'vue';

import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import getRestaurant from '@menu/Utils/getRestaurant';

const toast = useToast();
const router = useRouter();

const restaurant = getRestaurant();

const cartStore = useCartStore();

const selectedOrderTypeId = ref<number | null>(null);
const selectedOrderPaymentTypeId = ref<number | null>(null);
const customerName = ref('');
const customerPhone = ref('');
const customerEmail = ref('');
const customerAddress = ref('');
const customerTable = ref(
  (() => {
    try {
      return sessionStorage.getItem('table') || '';
    } catch (e) {
      return '';
    }
  })(),
);
const customerCarPlateNumber = ref('');

const isCustomerTableReadonly = ref(
  (() => {
    try {
      return Boolean(sessionStorage.getItem('table'));
    } catch (e) {
      return false;
    }
  })(),
);

const customerNote = ref('');

const placingOrder = ref(false);

const orderingConfiguration = computed(() => {
  const orderingConfiguration = restaurant.orderingConfiguration;

  if (!orderingConfiguration) {
    throw new Error('No ordering configuration found.');
  }

  return orderingConfiguration;
});

const orderTypes = computed(() => orderingConfiguration.value.orderTypes);
const orderPaymentTypes = computed(
  () => orderingConfiguration.value.orderPaymentTypes,
);

const cartPriceAdjustments = computed(() =>
  restaurant.priceAdjustments.map((priceAdjustment) => ({
    ...priceAdjustment,
    amount:
      Math.round(cartStore.cartItemsTotalPrice * priceAdjustment.rate) / 100,
  })),
);
const cartTotalPrice = computed(
  () =>
    cartStore.cartItemsTotalPrice +
    cartPriceAdjustments.value.reduce(
      (total, priceAdjustment) => total + priceAdjustment.amount,
      0,
    ),
);

const selectedOrderType = computed(() =>
  orderTypes.value.find(({ id }) => id === selectedOrderTypeId.value),
);
const selectedOrderPaymentType = computed(() =>
  orderPaymentTypes.value.find(
    ({ id }) => id === selectedOrderPaymentTypeId.value,
  ),
);

const showOrderTypeSelector = computed(() => {
  if (orderTypes.value.length === 1 && orderTypes.value[0].type === 'TABLE') {
    return false;
  }

  return orderTypes.value.length > 1;
});

const askName = computed(() => {
  return !!selectedOrderType.value?.isNameEnabled;
});

const askPhone = computed(() => {
  return !!selectedOrderType.value?.isPhoneEnabled;
});

const askEmail = computed(() => {
  return !!selectedOrderType.value?.isEmailEnabled;
});

const askAddress = computed(() => {
  return selectedOrderType.value?.type === 'DELIVERY';
});

onMounted(() => {
  selectedOrderTypeId.value =
    orderTypes.value.length === 1 ? orderTypes.value[0].id : null;

  selectedOrderPaymentTypeId.value =
    orderPaymentTypes.value.length > 0 ? orderPaymentTypes.value[0].id : null;

  prefillVisitorDetails();
});

const prefillVisitorDetails = () => {
  axios.get(`/api/visitors/${getVisitorUuid()}`).then((res) => {
    const visitor = res.data.data;

    if (!customerName.value && visitor.customerName) {
      customerName.value = visitor.customerName;
    }

    if (!customerPhone.value && visitor.customerPhone) {
      customerPhone.value = visitor.customerPhone;
    }

    if (!customerEmail.value && visitor.customerEmail) {
      customerEmail.value = visitor.customerEmail;
    }

    if (!customerAddress.value && visitor.customerAddress) {
      customerAddress.value = visitor.customerAddress;
    }
  });
};

const placeOrder = () => {
  if (!selectedOrderTypeId.value) {
    toast.error(trans('Please select an order type'), {
      timeout: 4000,
    });
    return;
  }

  placingOrder.value = true;

  axios
    .post('/api/orders', {
      restaurantId: restaurant.id,
      orderTypeId: selectedOrderTypeId.value,
      orderPaymentTypeId: selectedOrderPaymentTypeId.value,
      visitorUuid: getVisitorUuid(),
      items: cartStore.cartItems.map((cartItem) => ({
        productId: cartItem.product.id,
        variantId: cartItem.variant.id,
        modifierIds: cartItem.modifiers.map(({ id }) => id),
        quantity: cartItem.quantity,
      })),
      customerName: askName.value ? customerName.value : null,
      customerPhone: askPhone.value ? customerPhone.value : null,
      customerEmail: askEmail.value ? customerEmail.value : null,
      customerAddress: askAddress.value ? customerAddress.value : null,
      customerTable: customerTable.value,
      customerNote: [
        customerCarPlateNumber.value.length > 0
          ? `Car plate number: ${customerCarPlateNumber.value}`
          : null,
        customerNote.value,
      ]
        .filter(Boolean)
        .join('\n\n'),
    })
    .then((res) => {
      if (res.data.data.redirectToPayment) {
        router.push({
          name: 'orderPay',
          params: { orderUuid: res.data.data.uuid },
        });
      } else {
        router.push({
          name: 'order',
          params: { orderUuid: res.data.data.uuid },
        });
      }

      placingOrder.value = false;
      cartStore.clearCartItems();
    })
    .catch((err) => {
      const message = err.response.data.message || 'Something went wrong...';

      placingOrder.value = false;

      toast.error(message, {
        timeout: 4000,
      });

      throw new Error('Order failed: ' + message);
    });
};

const setQuantityForCartItem = (cartItem: CartItem, quantity: number) => {
  cartStore.setCartItemQuantity({
    productId: cartItem.product.id,
    variantId: cartItem.variant.id,
    modifierIds: cartItem.modifiers.map(({ id }) => id),
    quantity,
  });
};
</script>
